import { z } from 'zod/v3';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../actions/framework';
import { GraphModel } from './graph.model';
import { err, ok } from 'neverthrow';
import { Assert } from '@cotera/utilities';
import { PublishGraphAction } from './publish.action';
import { GRAPH_EVENTS } from './constants';
import type { Toaster } from '@cotera/client/app/components/ui';

export class AutoPublishAction extends BaseAction<{}, { t: string }[]> {
  title = 'Auto Publish';
  description = 'Automatically publish the graph';
  inputSchema = z.object({});
  discrete = true;
  events = [GRAPH_EVENTS.OPS_ADD_TO_STAGED];

  constructor(private readonly toast: Pick<Toaster, 'error'>) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    const graph = context.getInScopeModelOfType(GraphModel);
    return graph !== null && this.shouldRun(graph);
  }

  async execute(
    _: {},
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string }[]>> {
    const graph = context.getInScopeModelOfType(GraphModel);
    Assert.assert(graph !== null, 'Graph model not found');

    if (this.shouldRun(graph)) {
      const result = await context
        .getAction(PublishGraphAction)!
        .askOrExecute({}, context);
      if (result.isErr()) {
        this.toast.error('Failed to publish graph', {
          description: JSON.stringify(result.error),
        });
        return err(result.error);
      }
    }

    return ok({});
  }

  private shouldRun(graph: GraphModel): boolean {
    return (
      graph.autoPublish.snapshot() &&
      graph.publishable.snapshot() &&
      graph.hasUnpublishedOperations.snapshot()
    );
  }
}
